/*
 * Gallery Component
 *
 * Changes:
 * - Added offset of 2 images (starts at index 2 instead of 0)
 * - Container is wider than screen (w-[calc(100%+10rem)]) with overflow hidden
 * - Desktop shows 5 images, mobile shows 3 (outer images hidden on mobile)
 * - Click on images to enlarge with object-contain in a modal
 * - Removed image counter (1/21 style)
 * - Scrolls to gallery section when nav link is clicked
 * - Increased vertical padding (py-20)
 * - Enlarged images max 70% viewport on desktop, 80% on mobile
 * - Reduced mobile image sizes to fit 3 images on screen (200px/220px/200px)
 */

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { GalleryImage } from '../types/gallery';
import { Tour } from '../types/tours';

const Gallery = () => {
  const [tours, setTours] = useState<Tour[]>([]);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [filteredImages, setFilteredImages] = useState<GalleryImage[]>([]);
  const [selectedTour, setSelectedTour] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(2);
  const [isAnimating, setIsAnimating] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  const imagesPerPage = 5;

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedTour === 'all') {
      setFilteredImages(images);
    } else {
      setFilteredImages(images.filter(img => img.tour_id === selectedTour));
    }
    setCurrentIndex(2);
  }, [selectedTour, images]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [toursResult, imagesResult] = await Promise.all([
        supabase
          .from('tours')
          .select('id, name, slug')
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false }),
        supabase
          .from('gallery_images')
          .select(`
            *,
            tour:tours(id, name, slug)
          `)
          .order('created_at', { ascending: false })
      ]);

      if (toursResult.error) throw toursResult.error;
      if (imagesResult.error) throw imagesResult.error;

      setTours(toursResult.data || []);
      setImages(imagesResult.data || []);
      setFilteredImages(imagesResult.data || []);
    } catch (error) {
      console.error('Error fetching gallery data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentIndex < filteredImages.length - 1 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev + 1);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 2 && !isAnimating) {
      setIsAnimating(true);
      setCurrentIndex(prev => prev - 1);
      setTimeout(() => setIsAnimating(false), 500);
    }
  };

  const getVisibleImages = () => {
    const visible: (GalleryImage | null)[] = [];
    for (let i = 0; i < 5; i++) {
      const index = currentIndex - 2 + i;
      if (index >= 0 && index < filteredImages.length) {
        visible.push(filteredImages[index]);
      } else {
        visible.push(null);
      }
    }
    return visible;
  };

  const visibleImages = getVisibleImages();

  const isVideo = (url: string) => {
    return url.toLowerCase().match(/\.(mp4|webm|ogg|mov)$/);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <>
      {/* Enlarged Image Modal */}
      {enlargedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setEnlargedImage(null)}
        >
          <button
            onClick={() => setEnlargedImage(null)}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all duration-300"
            aria-label="Close enlarged image"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={enlargedImage}
            alt="Enlarged view"
            className="max-w-[80vw] max-h-[80vh] md:max-w-[70vw] md:max-h-[70vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="space-y-2">
      {/* Filter Buttons */}
      <div className="flex flex-wrap gap-3 justify-center items-center px-4">
        <button
          onClick={() => setSelectedTour('all')}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
            selectedTour === 'all'
              ? 'bg-white text-[#2A382A]'
              : 'bg-transparent text-white border-2 border-white hover:bg-white/10'
          }`}
        >
          All
        </button>

        {tours.slice(0, 8).map((tour) => (
          <button
            key={tour.id}
            onClick={() => setSelectedTour(tour.id)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              selectedTour === tour.id
                ? 'bg-white text-[#2A382A]'
                : 'bg-transparent text-white border-2 border-white hover:bg-white/10'
            }`}
          >
            {tour.name}
          </button>
        ))}
      </div>

      {/* Gallery Grid */}
      {filteredImages.length === 0 ? (
        <div className="text-center p-16 text-white/70">
          <p>No images available for this tour.</p>
        </div>
      ) : (
        <div className="relative">
          {/* Images Container - Overlapping Cards Layout */}
          <div className="flex items-center justify-center min-h-[400px] md:min-h-[600px] py-20">
            <div className="relative w-[calc(100%+10rem)] h-[400px] md:h-[550px] flex items-center justify-center overflow-hidden">
              {/* Far Left Background Image - Hidden on mobile */}
              {visibleImages[0] && (
                <div
                  onClick={() => setEnlargedImage(visibleImages[0].image_url)}
                  className="hidden md:block absolute left-0 w-[280px] h-[350px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 hover:z-30 cursor-pointer"
                  style={{
                    zIndex: 1,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: visibleImages[0] ? 1 : 0
                  }}
                >
                  {isVideo(visibleImages[0].image_url) ? (
                    <div className="relative w-full h-full bg-black/20 flex items-center justify-center">
                      <Play className="w-16 h-16 text-white/80" />
                      <video
                        src={visibleImages[0].image_url}
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                    </div>
                  ) : (
                    <img
                      src={visibleImages[0].image_url}
                      alt={visibleImages[0].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}

              {/* Left Image */}
              {visibleImages[1] && (
                <div
                  onClick={() => setEnlargedImage(visibleImages[1].image_url)}
                  className="absolute left-[5%] md:left-[200px] w-[200px] md:w-[320px] h-[280px] md:h-[400px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 hover:z-30 cursor-pointer"
                  style={{
                    zIndex: 2,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: visibleImages[1] ? 1 : 0
                  }}
                >
                  {isVideo(visibleImages[1].image_url) ? (
                    <div className="relative w-full h-full bg-black/20 flex items-center justify-center">
                      <Play className="w-16 h-16 text-white/80" />
                      <video
                        src={visibleImages[1].image_url}
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                    </div>
                  ) : (
                    <img
                      src={visibleImages[1].image_url}
                      alt={visibleImages[1].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}

              {/* Center Large Image */}
              {visibleImages[2] && (
                <div
                  onClick={() => setEnlargedImage(visibleImages[2].image_url)}
                  className="absolute left-1/2 transform -translate-x-1/2 w-[220px] md:w-[380px] h-[300px] md:h-[450px] rounded-3xl overflow-hidden shadow-2xl hover:scale-105 cursor-pointer"
                  style={{
                    zIndex: 10,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: visibleImages[2] ? 1 : 0
                  }}
                >
                  {isVideo(visibleImages[2].image_url) ? (
                    <div className="relative w-full h-full bg-black/20 flex items-center justify-center">
                      <Play className="w-20 h-20 text-white/80" />
                      <video
                        src={visibleImages[2].image_url}
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                    </div>
                  ) : (
                    <img
                      src={visibleImages[2].image_url}
                      alt={visibleImages[2].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}

              {/* Right Image */}
              {visibleImages[3] && (
                <div
                  onClick={() => setEnlargedImage(visibleImages[3].image_url)}
                  className="absolute right-[5%] md:right-[200px] w-[200px] md:w-[320px] h-[280px] md:h-[400px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 hover:z-30 cursor-pointer"
                  style={{
                    zIndex: 2,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: visibleImages[3] ? 1 : 0
                  }}
                >
                  {isVideo(visibleImages[3].image_url) ? (
                    <div className="relative w-full h-full bg-black/20 flex items-center justify-center">
                      <Play className="w-16 h-16 text-white/80" />
                      <video
                        src={visibleImages[3].image_url}
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                    </div>
                  ) : (
                    <img
                      src={visibleImages[3].image_url}
                      alt={visibleImages[3].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}

              {/* Far Right Background Image - Hidden on mobile */}
              {visibleImages[4] && (
                <div
                  onClick={() => setEnlargedImage(visibleImages[4].image_url)}
                  className="hidden md:block absolute right-0 w-[280px] h-[350px] rounded-3xl overflow-hidden shadow-2xl transform hover:scale-105 hover:z-30 cursor-pointer"
                  style={{
                    zIndex: 1,
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    opacity: visibleImages[4] ? 1 : 0
                  }}
                >
                  {isVideo(visibleImages[4].image_url) ? (
                    <div className="relative w-full h-full bg-black/20 flex items-center justify-center">
                      <Play className="w-16 h-16 text-white/80" />
                      <video
                        src={visibleImages[4].image_url}
                        className="absolute inset-0 w-full h-full object-cover opacity-50"
                      />
                    </div>
                  ) : (
                    <img
                      src={visibleImages[4].image_url}
                      alt={visibleImages[4].title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={handlePrev}
              disabled={currentIndex === 2 || isAnimating}
              className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#2A382A] transition-all duration-300 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white disabled:cursor-not-allowed"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={handleNext}
              disabled={currentIndex >= filteredImages.length - 1 || isAnimating}
              className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center text-white hover:bg-white hover:text-[#2A382A] transition-all duration-300 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-white disabled:cursor-not-allowed"
              aria-label="Next image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Gallery;
